import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IRACING_AUTH_API_BASE, IRACING_AUTH_LOGIN_URL, IRACING_PROD_API_BASE } from '../utils/iracingAuthApi';
import { MEMBER_CONSENT_CHANGE_EVENT } from '../utils/driverPrivacy';

// Home page "your stats" card. Most members' career stats are hidden unless they
// opt in, so instead of a customer ID search this points the visitor at their own
// profile: sign in with iRacing, link an existing site account, or open their stats.
const REFRESH_EVENTS = ['storage', 'focus', 'iracinglink-changed', MEMBER_CONSENT_CHANGE_EVENT];

// Remember the member's iRacing customer ID for their current login, so the card can
// still link to their stats when the account lookup fails. Tied to the token so a
// different login on the same browser never sees someone else's ID.
const CUST_ID_CACHE_KEY = 'homeProfileCustId';

function readCachedCustId(token) {
  try {
    const cached = JSON.parse(localStorage.getItem(CUST_ID_CACHE_KEY) || 'null');
    return cached && cached.token === token && cached.custId ? cached.custId : null;
  } catch (_) {
    return null;
  }
}

function writeCachedCustId(token, custId) {
  try {
    if (custId) localStorage.setItem(CUST_ID_CACHE_KEY, JSON.stringify({ token, custId }));
    else localStorage.removeItem(CUST_ID_CACHE_KEY);
  } catch (_) {
    // Storage can be unavailable (private mode); the card still works without the cache.
  }
}

// Read-only account lookup. In local development the auth API points at a local
// backend; when that isn't running (a network error, not an HTTP error), fall back
// to the live backend so a token from a live-site login still resolves.
async function fetchMemberConsent(token, signal) {
  const request = (base) =>
    fetch(`${base}/api/member-consent`, { headers: { Authorization: `Bearer ${token}` }, signal });
  try {
    return await request(IRACING_AUTH_API_BASE);
  } catch (err) {
    if (err.name === 'AbortError' || IRACING_AUTH_API_BASE === IRACING_PROD_API_BASE) throw err;
    return request(IRACING_PROD_API_BASE);
  }
}

const IracingMark = () => <img src="/iRacing-Icon-BW-White.png" alt="" className="hp-profile__mark" />;

const HomeProfileCard = () => {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  // status: 'signed-out' | 'loading' | 'linked' | 'unlinked' | 'error'
  const [account, setAccount] = useState({ status: token ? 'loading' : 'signed-out' });
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    const readToken = () => setToken(localStorage.getItem('token'));
    REFRESH_EVENTS.forEach((event) => window.addEventListener(event, readToken));
    return () => REFRESH_EVENTS.forEach((event) => window.removeEventListener(event, readToken));
  }, []);

  useEffect(() => {
    if (!token) {
      setAccount({ status: 'signed-out' });
      return undefined;
    }
    const controller = new AbortController();
    setAccount({ status: 'loading' });
    const load = async () => {
      try {
        const response = await fetchMemberConsent(token, controller.signal);
        if (!response.ok) throw new Error(`member-consent ${response.status}`);
        const data = await response.json();
        if (controller.signal.aborted) return;
        if (data.linked && data.custId) {
          writeCachedCustId(token, data.custId);
          setAccount({ status: 'linked', custId: data.custId, displayName: data.displayName });
        } else {
          writeCachedCustId(token, null);
          setAccount({ status: 'unlinked' });
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('home profile card load error:', err);
        setAccount({ status: 'error', custId: readCachedCustId(token) });
      }
    };
    load();
    return () => controller.abort();
  }, [token, reloadCount]);

  const handleLink = async () => {
    setLinking(true);
    setLinkError('');
    try {
      const response = await fetch(`${IRACING_AUTH_API_BASE}/api/auth/iracing/link-init`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok && data.authorizeUrl) {
        window.location.href = data.authorizeUrl;
        return;
      }
      setLinkError(data.message || 'Could not start linking. Try again from your account page.');
    } catch (err) {
      console.error('link-init error:', err);
      setLinkError('Could not reach the server. Try again from your account page.');
    }
    setLinking(false);
  };

  let title = 'Your career stats';
  let lede;
  let action = null;

  switch (account.status) {
    case 'linked':
      title = account.displayName || title;
      lede = 'Your race history, ratings and percentiles.';
      action = (
        <Link to={`/user/careerstats/${account.custId}`} className="hp-profile__cta hp-profile__cta--stats">
          View my career stats
        </Link>
      );
      break;
    case 'unlinked':
      lede = 'Link your iRacing account to see your stats.';
      action = (
        <button type="button" className="hp-profile__cta" onClick={handleLink} disabled={linking}>
          <IracingMark />
          {linking ? 'Opening iRacing…' : 'Link iRacing account'}
        </button>
      );
      break;
    case 'error':
      lede = 'Your account could not be loaded right now.';
      action = account.custId ? (
        <Link to={`/user/careerstats/${account.custId}`} className="hp-profile__cta hp-profile__cta--stats">
          Open my stats
        </Link>
      ) : (
        <button type="button" className="hp-profile__cta" onClick={() => setReloadCount((count) => count + 1)}>
          Try again
        </button>
      );
      break;
    case 'loading':
      lede = 'Loading your account…';
      break;
    default:
      lede = 'Sign in with iRacing to see your race history.';
      action = (
        <a href={IRACING_AUTH_LOGIN_URL} className="hp-profile__cta">
          <IracingMark />
          Sign in with iRacing
        </a>
      );
  }

  return (
    <section className="hp-profile" aria-labelledby="hp-profile-title" aria-busy={account.status === 'loading'}>
      <div className="hp-profile__text">
        <h1 id="hp-profile-title" className="hp-profile__title">
          {title}
        </h1>
        <p className="hp-profile__lede">{lede}</p>
        {linkError && (
          <p className="hp-profile__error" role="alert">
            {linkError}
          </p>
        )}
      </div>
      {action && <div className="hp-profile__actions">{action}</div>}
    </section>
  );
};

export default HomeProfileCard;
